import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Scissors, TreeDeciduous, Leaf, Droplets, Axe, Palette, 
  ArrowRight, Check, Phone, Euro, Loader2,
  Lightbulb, Compass, PenTool, Flower2, Home, Wrench,
  Footprints, Sun, Droplet, Search, Filter
} from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  scissors: Scissors,
  tree: TreeDeciduous,
  leaf: Leaf,
  droplets: Droplets,
  axe: Axe,
  palette: Palette,
  lightbulb: Lightbulb,
  compass: Compass,
  pen: PenTool,
  flower: Flower2,
  home: Home,
  wrench: Wrench,
  footprints: Footprints,
  sun: Sun,
  droplet: Droplet,
};

const ServicesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'eligible', 'not-eligible'

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        // Filter only active services
        const activeServices = res.data.filter(s => s.is_active !== false);
        setServices(activeServices);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location, services]);

  // Filter services based on search and filter type
  const filteredServices = services.filter(service => {
    // Search filter
    const matchesSearch = searchQuery === '' || 
      service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Type filter
    const matchesFilter = filterType === 'all' || 
      (filterType === 'eligible' && service.eligible_credit_impot !== false) ||
      (filterType === 'not-eligible' && service.eligible_credit_impot === false);
    
    return matchesSearch && matchesFilter;
  });

  // Get services eligible for tax credit from filtered results
  const eligibleServices = filteredServices.filter(s => s.eligible_credit_impot === true && s.category !== 'conseil' && s.category !== 'amenagement');
  const nonEligibleEntretienServices = filteredServices.filter(s => s.eligible_credit_impot !== true && s.category === 'entretien');
  const conseilServices = filteredServices.filter(s => s.category === 'conseil');
  const amenagementServices = filteredServices.filter(s => s.category === 'amenagement');

  const getIcon = (iconName) => {
    return iconMap[iconName] || Leaf;
  };

  // Navigate to appropriate page based on service category
  const goToServicePage = (category, title) => {
    // Check if it's Élagage (special case)
    if (title && title.toLowerCase().includes('élagage')) {
      navigate('/elagage');
      return;
    }
    
    switch(category) {
      case 'entretien':
        navigate('/entretien');
        break;
      case 'conseil':
        navigate('/conseil');
        break;
      case 'amenagement':
        navigate('/amenagement');
        break;
      default:
        navigate('/entretien');
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 py-20 pt-32 lg:pt-40 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-96 h-96 bg-emerald-400/20 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-yellow-400/10 rounded-full blur-[80px]"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
              Nos services de jardinage
            </h1>
            <p className="text-emerald-100 text-lg md:text-xl mb-8">
              Entretien régulier ou projets d'aménagement, nous avons la solution adaptée à vos besoins.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/entretien">
                <Button className="bg-white text-emerald-700 hover:bg-emerald-50 rounded-full px-8 py-4 font-semibold shadow-lg">
                  Réserver un entretien
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:0465011022">
                <Button variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10 rounded-full px-8 py-4">
                  <Phone className="w-5 h-5 mr-2" />
                  04 65 01 10 22
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter Bar */}
      <section className="bg-white border-b border-stone-200 sticky top-16 z-40 shadow-sm" data-testid="filter-bar">
        <div className="container-custom py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search Input */}
            <div className="relative flex-1 w-full md:max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <input
                type="text"
                placeholder="Rechercher un service..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-stone-200 rounded-full bg-stone-50 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Filter Buttons */}
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <span className="text-sm text-stone-500 hidden sm:block">
                <Filter className="w-4 h-4 inline mr-1" />
                Filtrer :
              </span>
              <button
                onClick={() => setFilterType('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filterType === 'all'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
                data-testid="filter-all"
              >
                Tous les services
              </button>
              <button
                onClick={() => setFilterType('eligible')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                  filterType === 'eligible'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md'
                    : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                }`}
                data-testid="filter-eligible"
              >
                <Euro className="w-3.5 h-3.5" />
                Avec avance immédiate
              </button>
              <button
                onClick={() => setFilterType('not-eligible')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filterType === 'not-eligible'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                }`}
                data-testid="filter-not-eligible"
              >
                Sur devis
              </button>
            </div>

            {/* Results count */}
            <div className="text-sm text-stone-500">
              {filteredServices.length} service{filteredServices.length > 1 ? 's' : ''} trouvé{filteredServices.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </section>

      {/* Tax Credit Banner */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 py-4">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 text-white text-center">
            <Euro className="w-6 h-6" />
            <p className="font-semibold">
              Avance immédiate du crédit d'impôt : <span className="underline">50% pris en charge</span> sur les services d'entretien éligibles
            </p>
            <Link to="/credit-impot" className="text-amber-100 hover:text-white underline text-sm">
              En savoir plus →
            </Link>
          </div>
        </div>
      </section>

      {/* No Results Message */}
      {!loading && filteredServices.length === 0 && (
        <section className="py-20 bg-white">
          <div className="container-custom text-center">
            <div className="max-w-md mx-auto">
              <Search className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-stone-700 mb-2">Aucun service trouvé</h3>
              <p className="text-stone-500 mb-6">
                Essayez de modifier votre recherche ou vos filtres.
              </p>
              <Button 
                onClick={() => { setSearchQuery(''); setFilterType('all'); }}
                variant="outline"
                className="rounded-full"
              >
                Réinitialiser les filtres
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Eligible Services - Crédit d'impôt */}
      {eligibleServices.length > 0 && (filterType === 'all' || filterType === 'eligible') && (
      <section className="py-20 bg-white" id="services-eligibles">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Euro className="w-4 h-4" />
              Éligibles au crédit d'impôt 50%
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-stone-900 mb-4">
              Services d'entretien
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Tous ces services bénéficient de l'<strong>avance immédiate du crédit d'impôt</strong>. 
              Vous ne payez que 50% du montant !
            </p>
          </div>

          <div className="space-y-20">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
              </div>
            ) : eligibleServices.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <p>Aucun service disponible pour le moment.</p>
              </div>
            ) : (
              eligibleServices.map((service, index) => {
                const ServiceIcon = getIcon(service.icon);
                const serviceSlug = service.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                return (
                  <div 
                    key={service.id}
                    id={serviceSlug}
                    className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center scroll-mt-32 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                  >
                    <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                      <div className="relative">
                        <img 
                          src={service.image_url}
                          alt={service.title}
                          className="rounded-3xl shadow-2xl w-full h-80 object-cover"
                        />
                        {/* Badge Crédit d'impôt */}
                        <div className="absolute top-4 left-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg flex items-center gap-2">
                          <Euro className="w-4 h-4" />
                          50% pris en charge
                        </div>
                      </div>
                    </div>
                    
                    <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center">
                          <ServiceIcon className="w-7 h-7 text-emerald-600" />
                        </div>
                      </div>
                      
                      <h3 className="font-heading font-bold text-2xl md:text-3xl text-stone-900 mb-4">
                        {service.title}
                      </h3>
                      
                      <p className="text-stone-600 text-lg mb-6">
                        {service.description}
                      </p>
                      
                      {service.features && service.features.length > 0 && (
                        <ul className="space-y-3 mb-8">
                          {service.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-3 text-stone-700">
                              <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Badge éligibilité */}
                      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-6">
                        <div className="flex items-start gap-3">
                          <Euro className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-emerald-800 text-sm">Éligible Avance Immédiate Crédit d'Impôt</p>
                            <p className="text-emerald-700 text-sm">Vous bénéficiez de 50% pris en charge par l'État</p>
                          </div>
                        </div>
                      </div>
                      
                      <Link to="/entretien">
                        <Button className="btn-primary rounded-full px-8">
                          Réserver ce service
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>
      )}

      {/* Services sur devis - Conseil & Conception */}
      {(filterType === 'all' || filterType === 'not-eligible') && conseilServices.length > 0 && (
      <section className="py-20 bg-white" id="conseil-conception">
        <div className="container-custom">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Lightbulb className="w-4 h-4" />
              Sur devis personnalisé
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-stone-900 mb-4">
              Conseils & Études
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Nos experts paysagistes vous accompagnent dans la conception de votre projet de jardin idéal.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {conseilServices.map((service) => {
              const ServiceIcon = getIcon(service.icon);
              return (
                <div 
                  key={service.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-stone-200 group cursor-pointer"
                  onClick={() => goToServicePage('conseil', service.title)}
                >
                  <div className="relative h-40">
                    <img 
                      src={service.image_url}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 left-3 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Conseil
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                        <ServiceIcon className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="font-semibold text-stone-900">{service.title}</h3>
                    </div>
                    <p className="text-stone-500 text-sm mb-3 line-clamp-2">{service.description}</p>
                    <p className="text-blue-600 text-sm font-medium flex items-center gap-1">
                      Demander un devis gratuit
                      <ArrowRight className="w-4 h-4" />
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Services sur devis - Élagage et autres services non éligibles */}
      {(filterType === 'all' || filterType === 'not-eligible') && nonEligibleEntretienServices.length > 0 && (
      <section className="py-20 bg-white" id="elagage-services">
        <div className="container-custom">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Axe className="w-4 h-4" />
              Sur devis personnalisé
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-stone-900 mb-4">
              Élagage & Travaux spécialisés
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Services professionnels nécessitant une évaluation sur place. Devis gratuit et intervention sécurisée.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {nonEligibleEntretienServices.map((service) => {
              const ServiceIcon = getIcon(service.icon);
              return (
                <div 
                  key={service.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-stone-200 group cursor-pointer"
                  onClick={() => goToServicePage('elagage', service.title)}
                >
                  <div className="relative h-40">
                    <img 
                      src={service.image_url}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 left-3 bg-amber-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Élagage
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center group-hover:bg-amber-500 transition-colors">
                        <ServiceIcon className="w-5 h-5 text-amber-600 group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="font-semibold text-stone-900">{service.title}</h3>
                    </div>
                    <p className="text-stone-500 text-sm mb-3 line-clamp-2">{service.description}</p>
                    <p className="text-amber-600 text-sm font-medium flex items-center gap-1">
                      Demander un devis gratuit
                      <ArrowRight className="w-4 h-4" />
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Services sur devis - Aménagements */}
      {(filterType === 'all' || filterType === 'not-eligible') && amenagementServices.length > 0 && (
      <section className="py-20 bg-stone-50" id="amenagements">
        <div className="container-custom">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Wrench className="w-4 h-4" />
              Sur devis personnalisé
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-stone-900 mb-4">
              Aménagements & Installations
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              De la création de jardin à l'installation de terrasses, nous réalisons tous vos projets d'aménagement extérieur.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {amenagementServices.map((service) => {
              const ServiceIcon = getIcon(service.icon);
              return (
                <div 
                  key={service.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-stone-200 group cursor-pointer"
                  onClick={() => goToServicePage('amenagement', service.title)}
                >
                  <div className="relative h-40">
                    <img 
                      src={service.image_url}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 left-3 bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Aménagement
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-500 transition-colors">
                        <ServiceIcon className="w-5 h-5 text-purple-600 group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="font-semibold text-stone-900">{service.title}</h3>
                    </div>
                    <p className="text-stone-500 text-sm mb-3 line-clamp-2">{service.description}</p>
                    <p className="text-purple-600 text-sm font-medium flex items-center gap-1">
                      Demander un devis gratuit
                      <ArrowRight className="w-4 h-4" />
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-emerald-600">
        <div className="container-custom text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
            Prêt à transformer votre jardin ?
          </h2>
          <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
            Contactez-nous pour un devis gratuit ou réservez directement en ligne.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/entretien">
              <Button className="bg-white text-emerald-600 hover:bg-stone-100 rounded-full px-10 py-5 font-bold text-lg shadow-xl">
                Réserver un entretien
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0465011022">
              <Button variant="outline" className="bg-transparent text-white border-white/50 hover:bg-white/10 rounded-full px-10 py-5 font-semibold text-lg">
                <Phone className="w-5 h-5 mr-2" />
                04 65 01 10 22
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServicesPage;
